// Plain module (not a component), mirroring detailTabs.ts's own doc comment
// on why this stays out of a .tsx file - exporting these alongside a
// component would trip the react-refresh/only-export-components rule.

import { PRACTICE_DETAIL_GROUPS } from './fieldDefinitions';

export interface DetailSectionDef {
    id: string;
    label: string;
}

export const DETAIL_SECTIONS = [
    { id: 'info', label: 'Info' },
    { id: 'files', label: 'Files' },
    { id: 'beta', label: 'Beta access' }
] as const satisfies readonly DetailSectionDef[];

export type DetailSectionId = (typeof DETAIL_SECTIONS)[number]['id'];

// Info is the left-rail's landing pane - a bare `?tab=details` (no
// `?section=`) lands here, same "missing param falls back to the first tab"
// convention as DEFAULT_TAB in detailTabs.ts.
export const DEFAULT_DETAIL_SECTION: DetailSectionId = 'info';

const DETAIL_SECTION_IDS = DETAIL_SECTIONS.map((section) => section.id);

export function isDetailSection(value: string): value is DetailSectionId {
    return (DETAIL_SECTION_IDS as readonly string[]).includes(value);
}

// Which section pane a given form field key renders in - since the Info and
// Account panes were merged, every form field (Practice, Doctor, and Account
// groups alike) lives in the single Info pane. Derived from
// PRACTICE_DETAIL_GROUPS so an unknown key still falls through to the
// default. Files/Beta access carry no form fields, so no key ever maps to
// either of those ids.
const FIELD_KEY_TO_SECTION: Record<string, DetailSectionId> = Object.fromEntries(
    PRACTICE_DETAIL_GROUPS.flatMap((group) =>
        group.fields.map((field) => [field.key, 'info'] as const)
    )
);

/**
 * Used by the section-switching wiring around scrollToField (ErrorBanner's
 * "Fix X", header's onAddField, DetailAside's "Needs attention" rows) - see
 * PracticeDetail's scrollToFieldKeyed. Falls back to the default section for
 * an unknown key rather than throwing, since a stale/renamed field key
 * should never crash the page.
 */
export function sectionForField(fieldKey: string): DetailSectionId {
    return FIELD_KEY_TO_SECTION[fieldKey] ?? DEFAULT_DETAIL_SECTION;
}
